import { useCallback, useEffect, useRef, useState } from 'react';
import { Collapse } from '@mui/material';
import { useAuth } from '../../auth/AuthContext';
import { getCobroPorTramite } from '../../services/cobranzaService';
import { colorPrimarioTramite } from '../Documentos/coloresTramite';
import { ChipEstadoCobro } from './estadoCobro';
import { formatoMoneda } from './PanelesCobro';
import { PAGO_TODO, esPagoTodo } from './resumenCobro';
import { leerPreferenciaAbierta, guardarPreferenciaAbierta } from './preferenciaAbierta';
import DetalleCobranza from './DetalleCobranza';

const COLOR = colorPrimarioTramite('cobro');

// La tarjeta arranca cerrada y se acuerda de cómo la dejó la persona, entre trámites y recargas: quien
// trabaja cobros la quiere abierta siempre, y quien no, cerrada siempre.
const CLAVE_ABIERTA = 'cobranzaTramite.abierta';

/**
 * Cobranza de un trámite, en su propia ficha — justo debajo del cartel de estado final del trámite
 * (Completo / Cerrado favorable), que es cuando aparece. A diferencia de la lista compacta que se ve en
 * la ficha del cliente (TarjetaCobrosCliente), acá el cobro tiene lugar propio.
 *
 * Es desplegable: el encabezado (estado y saldo del cobro) se ve siempre, y el detalle se abre y se
 * cierra con un clic. Así queda arriba, a mano, sin empujar hacia abajo el resto de la ficha
 * (comentarios, progreso, eventos).
 *
 * Adentro no hay pestañas: lo dibuja DetalleCobranza (un Resumen y tres secciones que se abren y
 * cierran por separado — Información del pacto, Plan de cuotas y pagos, y Bitácora), el mismo
 * componente que usan el modal del cliente y la atención de un turno de cobro. Cobrar una cuota,
 * ver el pacto o anotar una gestión se hace sin cambiar de pantalla.
 *
 * Solo aparece una vez que el cobro existe (el trámite cerró favorable — ver CambioEstadoService,
 * backend). Antes de eso, la ficha muestra en su lugar el editor de pacto (TarjetaCobroTramite).
 */
// Cobranzas está en prueba: hoy la ven ADMIN y PROGRAMADOR (ver PERMISOS_EXCLUSIVOS en
// auth/roles.js). El gateo va acá adentro y no en cada ficha que monta el componente: son once
// vistas de trámite más la del cliente, y alcanzaba con olvidarse de una para que la sección
// quedara visible.
export default function SeccionCobranzaTramite({ tramite }) {
    const { puede } = useAuth();
    // Desde el 16-oct-2026 la ve cualquiera que opere cobros (todos los roles): pagar y reprogramar
    // cuotas es de todos; la configuración se gatea adentro (permisosCobro.js).
    const puedeVerCobranzas = puede && (puede('cobranzas.cobrar') || puede('cobranzas.editar'));

    const idTramite = tramite?.idTramite;
    const [cobro, setCobro] = useState(null);
    const [abierta, setAbierta] = useState(() => leerPreferenciaAbierta(CLAVE_ABIERTA, false));

    // Si se pide otro cobro antes de que llegue el anterior (se cambió de trámite), la respuesta vieja se descarta.
    const pedidoActual = useRef(0);

    const cargar = useCallback(() => {
        if (!idTramite) return;
        const pedido = ++pedidoActual.current;
        getCobroPorTramite(idTramite)
            .then((datos) => { if (pedido === pedidoActual.current) setCobro(datos); })
            .catch(() => { if (pedido === pedidoActual.current) setCobro(null); });
    }, [idTramite]);

    useEffect(() => {
        if (!puedeVerCobranzas) return;
        // Trámite nuevo: no se muestra el cobro del anterior mientras carga el suyo. Las recargas después
        // de guardar algo (cargar a secas) NO pasan por acá, así la tarjeta no parpadea ni se cierra.
        setCobro(null);
        cargar();
    }, [cargar, puedeVerCobranzas]);

    const alternar = () => {
        const siguiente = !abierta;
        setAbierta(siguiente);
        guardarPreferenciaAbierta(CLAVE_ABIERTA, siguiente);
    };

    if (!puedeVerCobranzas) return null;
    if (!cobro) return null;

    const idDetalle = `cobranza-tramite-${idTramite}`;

    return (
        <div className="row mb-3">
            <div className="col-12">
                <div className="card shadow-lg border-0 rounded-4 px-4 py-3">
                    {/* Un botón de verdad (no un div con onClick) para que se pueda usar con teclado y lo
                        anuncien los lectores de pantalla. El chip y el saldo van adentro: son <span>. */}
                    <button
                        type="button"
                        className="border-0 bg-transparent p-0 w-100 d-flex justify-content-between align-items-center flex-wrap gap-2 text-start"
                        onClick={alternar}
                        aria-expanded={abierta}
                        aria-controls={idDetalle}
                    >
                        <span className="fw-bold fs-4" style={{ color: COLOR }}>
                            <i className="bi bi-cash-coin me-2"></i>Cobranza
                        </span>
                        <span className="d-flex align-items-center gap-3">
                            <ChipEstadoCobro estado={cobro.estado} />
                            <span className="fw-bold fs-5" style={{ color: COLOR }}>{esPagoTodo(cobro) ? PAGO_TODO : formatoMoneda(cobro.saldo)}</span>
                            <i
                                className="bi bi-chevron-down"
                                aria-hidden="true"
                                style={{ color: '#64748b', transition: 'transform 0.2s ease', transform: abierta ? 'rotate(180deg)' : 'none' }}
                            ></i>
                        </span>
                    </button>

                    <Collapse in={abierta} unmountOnExit>
                        <div id={idDetalle} className="pt-3">
                            <DetalleCobranza cobro={cobro} onCambio={cargar} />
                        </div>
                    </Collapse>
                </div>
            </div>
        </div>
    );
}
